import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

const DESKTOP_BACKGROUND = "/media/images/desktop_background.jpg";
const MOBILE_BACKGROUND = "/media/images/mobile_background.jpg";

function PortalCard({ panel, isActive, onPlaceholderClick }) {
  const imageContent = (
    <>
      {!panel.placeholder && (
        <img
          src={panel.image}
          alt={panel.imageAlt}
          width={panel.imageWidth}
          height={panel.imageHeight}
          loading="eager"
          decoding="async"
        />
      )}
      <div className="portal-card-shade" aria-hidden="true" />
    </>
  );

  return (
    <article className={`portal-card${isActive ? " is-active" : ""}`}>
      {panel.placeholder ? (
        <div
          className="portal-card-image is-placeholder"
          aria-label="Reserved empty panel"
        >
          {imageContent}
        </div>
      ) : panel.href ? (
        <Link
          to={panel.href}
          className="portal-card-image portal-card-link"
          data-testid={`portal-card-${panel.id}`}
          aria-label={`${panel.title}: ${panel.actionLabel}`}
        >
          {imageContent}
        </Link>
      ) : (
        <button
          type="button"
          className="portal-card-image portal-card-link"
          data-testid={`portal-card-${panel.id}`}
          aria-label={`${panel.title}: ${panel.actionLabel}`}
          onClick={() => onPlaceholderClick(panel.title)}
        >
          {imageContent}
        </button>
      )}
      {panel.placeholder ? (
        <div
          className="portal-card-content is-placeholder"
          aria-hidden="true"
        />
      ) : (
        <div className="portal-card-content">
          <p>{panel.eyebrow}</p>
          <h2>{panel.title}</h2>
        </div>
      )}
    </article>
  );
}

const MainLanding = ({ landingData }) => {
  const panels = landingData?.panels || [];
  const [carouselApi, setCarouselApi] = useState(null);
  const [activePanelIndex, setActivePanelIndex] = useState(0);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (!carouselApi) return undefined;

    const updateActivePanel = () => {
      const rootBounds = carouselApi.rootNode().getBoundingClientRect();
      const carouselCenter = rootBounds.left + rootBounds.width / 2;
      const closestIndex = carouselApi.slideNodes().reduce(
        (closest, slide, index) => {
          const bounds = slide.getBoundingClientRect();
          const distance = Math.abs(
            bounds.left + bounds.width / 2 - carouselCenter,
          );
          return distance < closest.distance ? { index, distance } : closest;
        },
        { index: 0, distance: Number.POSITIVE_INFINITY },
      ).index;

      setActivePanelIndex((current) =>
        current === closestIndex ? current : closestIndex,
      );
    };

    carouselApi.on("scroll", updateActivePanel);
    carouselApi.on("select", updateActivePanel);
    carouselApi.on("reInit", updateActivePanel);
    updateActivePanel();

    return () => {
      carouselApi.off("scroll", updateActivePanel);
      carouselApi.off("select", updateActivePanel);
      carouselApi.off("reInit", updateActivePanel);
    };
  }, [carouselApi]);

  const showPlaceholderMessage = (title) => {
    setAnnouncement(`${title} is coming soon.`);
  };

  const scrollCarousel = (direction) => {
    direction === "previous"
      ? carouselApi?.scrollPrev()
      : carouselApi?.scrollNext();
  };

  const scrollToPanel = (index) => {
    carouselApi?.scrollTo(index);
  };

  return (
    <main className="main-landing" data-testid="main-landing">
      <picture className="portal-background" aria-hidden="true">
        <source media="(min-width: 768px)" srcSet={DESKTOP_BACKGROUND} />
        <img
          src={MOBILE_BACKGROUND}
          alt=""
          width="941"
          height="1672"
          decoding="async"
          fetchpriority="high"
        />
      </picture>
      <div className="portal-vignette" aria-hidden="true" />

      <div className="portal-content">
        <header className="portal-heading">
          <h1>
            <img
              className="portal-title-image"
              src="/media/images/title_text.JPEG"
              alt="Enter the World of the Pehuans"
              width="1774"
              height="887"
              decoding="async"
              fetchpriority="high"
            />
          </h1>
        </header>

        <section
          className="portal-carousel-section"
          aria-labelledby="portal-paths-title"
        >
          <div className="portal-carousel-header">
            <h2
              id="portal-paths-title"
              className="portal-carousel-title"
              aria-label="Read the Novel. Explore the Lore. Enter the Realm."
            >
              <span className="portal-carousel-title-track" aria-hidden="true">
                <span className="portal-carousel-title-group">
                  <span>Read the Novel</span>
                  <span className="portal-carousel-title-separator">.</span>
                  <span>Explore the Lore</span>
                  <span className="portal-carousel-title-separator">.</span>
                  <span>Enter the Realm</span>
                </span>
                <span className="portal-carousel-title-group">
                  <span>Read the Novel</span>
                  <span className="portal-carousel-title-separator">.</span>
                  <span>Explore the Lore</span>
                  <span className="portal-carousel-title-separator">.</span>
                  <span>Enter the Realm</span>
                </span>
              </span>
            </h2>
          </div>

          <Carousel
            className="portal-carousel"
            opts={{
              align: "center",
              dragFree: false,
              loop: true,
              slidesToScroll: 1,
            }}
            setApi={setCarouselApi}
            aria-label="Explore The Oath of The Unhuan"
          >
            <CarouselContent className="portal-carousel-track">
              {panels.map((panel, index) => (
                <CarouselItem className="portal-carousel-item" key={panel.id}>
                  <PortalCard
                    panel={panel}
                    isActive={activePanelIndex === index}
                    onPlaceholderClick={showPlaceholderMessage}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <div className="portal-carousel-controls-section">
            <div className="portal-carousel-dots" aria-label="Carousel position">
              {panels.map((panel, index) => (
                <button
                  type="button"
                  className={`portal-carousel-dot${
                    activePanelIndex === index ? " is-active" : ""
                  }`}
                  key={panel.id}
                  onClick={() => scrollToPanel(index)}
                  aria-label={`${panel.title || "Panel"} ${index + 1} of ${
                    panels.length
                  }`}
                  aria-current={
                    activePanelIndex === index ? "true" : undefined
                  }
                />
              ))}
            </div>

            <div className="portal-carousel-controls">
              <button
                type="button"
                aria-label="Previous path"
                data-testid="portal-carousel-previous"
                onClick={() => scrollCarousel("previous")}
              >
                <ArrowLeft aria-hidden="true" />
                <span>Previous</span>
              </button>
              <button
                type="button"
                aria-label="Next path"
                data-testid="portal-carousel-next"
                onClick={() => scrollCarousel("next")}
              >
                <span>Next</span>
                <ArrowRight aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>

        <p className="sr-only" aria-live="polite">
          {announcement}
        </p>
      </div>
    </main>
  );
};

export default MainLanding;
